import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { describeError } from '@london-life/errors';
import type { LifeView } from '@london-life/contracts';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { notify } from './alerts';

// The phone's full-screen apps share one shape: a frame (back, title, close, Escape closes it) and one way of
// loading the app's reply and running an action on it (busy while it runs, the message shown, the wallet
// refreshed, a failure explained).

export type PhoneAppProps = {
  onClose: () => void;
  // the player's life and the data saver setting (apps that draw a 3D room)
  life?: LifeView;
  dataSaver?: boolean;
  onWallet: () => void;
  // the map's card for a place (to go there), and doing something where you are
  onPlace?: (placeId: string) => void;
  onDo?: (activityId: string, choice?: string) => void;
  // a message to a player (the chat opens), and a page inside the app to open first (a shared link)
  onChat?: (lifeId: string) => void;
  open?: string;
};
type Reply = { message?: string };

// Escape closes the app (as every phone app does)
export function useEscapeClose(onClose: () => void) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
}

export function usePhoneApp<R extends Reply>(fetch: () => Promise<R>, { onClose, onWallet }: PhoneAppProps) {
  const [reply, setReply] = useState<R | null>(null);
  const [failure, setFailure] = useState<ReturnType<typeof describeError> | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setReply(await fetch());
      setFailure(null);
    } catch (error) {
      setFailure(describeError(error));
    }
    // the app's fetch is fixed for its life
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEscapeClose(onClose);
  // an action: true when it worked
  const run = async (work: () => Promise<R>) => {
    setBusy(true);
    try {
      const next = await work();
      setReply(next);
      if (next.message) notify(next.message);
      onWallet();
      return true;
    } catch (error) {
      notify(describeError(error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { reply, setReply, failure, busy, load, run };
}

export function PhoneAppFrame({
  id,
  title,
  className,
  closeLabel,
  back,
  onClose,
  failure,
  onRetry,
  loading,
  children,
}: {
  id: string;
  title: ReactNode;
  className: string;
  closeLabel: string;
  // where the back button goes (the phone, unless a page inside the app is open)
  back?: { label: string; onClick: () => void };
  onClose: () => void;
  failure: ReturnType<typeof describeError> | null;
  onRetry: () => void;
  // shown until the first reply
  loading?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={`profile-screen business-screen ${className}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${id}-title`}
    >
      <div className="profile-frame">
        <header className="profile-header">
          <button type="button" className="profile-back" onClick={back?.onClick ?? onClose}>
            {back?.label ?? '‹ Phone'}
          </button>
          <h2 id={`${id}-title`}>{title}</h2>
          <button type="button" className="life-close" aria-label={closeLabel} onClick={onClose}>
            ×
          </button>
        </header>
        <div className="profile-body">
          {failure && <ErrorNotice error={failure} onRetry={onRetry} />}
          {loading && !failure && <p className="business-muted">{loading}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}
